/**
 * Mean score of the evaluator's trace runs over time. A dashed line with the
 * version name marks where the evaluator itself changed, so an edit is not
 * mistaken for the agent getting better or worse. The latest value is on the right.
 */

import { ChartLine } from "lucide-react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Box } from "@/components/ui/box";
import { ChartTooltip } from "@/components/ui/chart-tooltip";
import { formatPeriod } from "@/features/experiments/detail/trace-run";
import { formatDate, formatScore } from "@/lib/format";
import type { EvaluatorRun } from "./types";

interface Point {
  at: string;
  value: number;
  version: number;
  period: string;
}

/** Experiments with a score, oldest first (a live pull by its period's end). */
export function chartPoints(runs: EvaluatorRun[]): Point[] {
  return runs
    .filter((r) => r.mean !== null)
    .map((r) => ({
      at: r.period?.end ?? r.created_at,
      value: r.mean ?? 0,
      version: r.evaluator_version,
      period: formatPeriod(r.period),
    }))
    .sort((a, b) => a.at.localeCompare(b.at));
}

/** The points where the evaluator version differs from the one before. */
export const versionChanges = (points: Point[]): Point[] =>
  points.filter((p, i) => i > 0 && points[i - 1]?.version !== p.version);

export function RunsChart({ runs, name }: { runs: EvaluatorRun[]; name: string }) {
  const points = chartPoints(runs);
  const latest = points[points.length - 1];
  return (
    <Box className="flex items-stretch gap-4 px-4 pt-3.5 pb-2">
      <div className="flex min-w-0 flex-1 flex-col">
        <h2 className="text-sm font-semibold">{name} on traces</h2>
        {points.length < 2 ? (
          <div className="my-3 flex flex-col items-center gap-2 rounded-lg border border-dashed border-line bg-well px-4 py-7 text-center">
            <ChartLine size={18} strokeWidth={1.8} className="text-dim" aria-hidden />
            <p className="max-w-sm text-[13px] leading-relaxed text-muted">
              {points.length === 0
                ? "No finished trace runs yet. The trend shows here once one completes."
                : "Only one trace run so far. Run another to see a trend."}
            </p>
          </div>
        ) : (
          <div role="img" aria-label={`Mean score across ${points.length} trace runs`} className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 16, right: 16, bottom: 0, left: -12 }}>
                <CartesianGrid stroke="var(--color-line-soft)" vertical={false} />
                <XAxis
                  dataKey="at"
                  tickFormatter={(v: string) => formatDate(v)}
                  tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, 1]}
                  tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <ChartTooltip
                  formatter={(v) => formatScore(typeof v === "number" ? v : null)}
                  labelFormatter={(_, p) => (p[0]?.payload as Point | undefined)?.period ?? ""}
                />
                {versionChanges(points).map((p) => (
                  <ReferenceLine
                    key={p.at}
                    x={p.at}
                    stroke="var(--color-faint)"
                    strokeDasharray="4 4"
                    label={{
                      value: `v${p.version}`,
                      position: "top",
                      fontSize: 11,
                      fill: "var(--color-muted)",
                    }}
                  />
                ))}
                <Line
                  type="linear"
                  dataKey="value"
                  stroke="var(--color-ink)"
                  strokeWidth={1.8}
                  dot={{ r: 4, fill: "var(--color-ink)" }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {latest && (
        <div
          className="flex shrink-0 flex-col items-end justify-center gap-0.5 pr-2"
          aria-label="Latest score"
        >
          <span className="font-mono text-[32px] leading-none font-semibold tracking-[-0.02em]">
            {formatScore(latest.value)}
          </span>
          <span className="text-xs text-muted">
            Latest · v{latest.version} · {latest.period}
          </span>
        </div>
      )}
    </Box>
  );
}
